import {
  Dialog,
  DialogActions,
  Button,
  Typography,
  Box,
  TextField,
  Snackbar,
  Tooltip,
  IconButton,
} from "@mui/material";
import MuiAlert from "@mui/material/Alert";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { PendingReceiptItem } from "./pendingReceipt/PendingReceiptTable";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { ja } from "date-fns/locale";
import { ProductInfoDialogStyle as styles } from "./ProductInfoDialogStyle";
import BaseFilterSelect from "./BaseFilter";
import UserFilterSelect from "./UserFilter";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { userSearchOptionsSelector } from "../recoil/selectors/userSearchOptionsSelector";
import { baseSearchOptionsSelector } from "../recoil/selectors/baseSearchOptionsSelector";
import { statusListSelector } from "../recoil/selectors/statusListSelector";
import StatusSelect from "./StatusSelect";
import ProductSetRow from "./ProductSetRow";
import { SetRowValues } from "../hooks/logic/useProductRegistration";
import NoteEditor, { Note } from "./NoteEditor";
import { loginUserSelector } from "../recoil/selectors/loginUserSelector";
import { useApiUpdateAmazonItems } from "../hooks/api/useApiUpdateAmazonItems";
import {
  RequestChatworkSendMessageRequest,
  RequestCheckSellerIdRequest,
  RequestGetAmazonItemsListRequest,
  RequestGetListingsItemsRequest,
  RequestSetItem,
  RequestUpdateAmazonItemsRequest,
  ResponseListingsItemResponse,
} from "../services/OpenAPI";
import { pendingReceiptUpdatedFlagState } from "../recoil/atoms/pendingReceiptAtoms";
import { pendingPackagingUpdatedFlagState } from "../recoil/atoms/pendingPackaging";
import { pendingPlanUpdatedFlagState } from "../recoil/atoms/pendingPlanAtoms";
import { needsReviewUpdatedFlagState } from "../recoil/atoms/needsReviewAtoms";
import { trustBoxUpdatedFlagState } from "../recoil/atoms/trustBoxAtoms";
import { useNavigate } from "react-router-dom";
import DeliveryUnitSelect from "./DeliveryUnitSelect";
import { useApiChatworkSend } from "../hooks/api/useApiChatworkSend";
import { Status } from "../constants/status";
import { useApiGetAmazonItems } from "../hooks/api/useApiGetAmazonItems";
import ConfirmDialog from "./ConfirmDialog";
import { dateFormat2 } from "../utils/dateFormat2";
import { ClearIcon } from "@mui/x-date-pickers";
import { useApiGetListingsItems } from "../hooks/api/useApiGetListingsItems";
import { useApiCheckSellerId } from "../hooks/api/useApiCheckSellerId";
import {
  ListingsSyncSeverity,
  useListingsSync,
} from "../hooks/logic/useListingsSync";
import ListingsItemDialog from "./ListingsItemDialog";
import { formatYMD } from "../utils/formatDateToString";
import { toDisplayFnsku, withFnskuSuffixOf } from "../utils/fnsku";
import {
  isGeneralUser,
  isNotGeneralUser,
  isNotOwner,
} from "../constants/roles";

type Props = {
  open: boolean;
  onClose: () => void;
  onConfirm?: () => void;
  confirmText?: string;
  cancelText?: string;
  hideCancel?: boolean;
  statusId: number;
  title?: string;
  parentItem?: PendingReceiptItem;
  childItems?: PendingReceiptItem[];
  onSuccess?: () => void;
};

const ProductInfoDialog: React.FC<Props> = ({
  open,
  title,
  parentItem,
  childItems = [],
  onClose,
  onConfirm,
  confirmText = "OK",
  cancelText = "",
  hideCancel = false,
  statusId,
}) => {
  const navigate = useNavigate();
  const { updateAmazonItems, isLoading: isUpdateLoading } =
    useApiUpdateAmazonItems();
  const { fetchAmazonItems } = useApiGetAmazonItems();
  const { sendChatwoekMessage } = useApiChatworkSend();

  const setPendingReceiptUpdatedFlag = useSetRecoilState(
    pendingReceiptUpdatedFlagState
  );
  const setPendingPackagingUpdatedFlag = useSetRecoilState(
    pendingPackagingUpdatedFlagState
  );
  const setPendingPlanUpdatedFlag = useSetRecoilState(
    pendingPlanUpdatedFlagState
  );
  const setNeedsReviewUpdateFlag = useSetRecoilState(
    needsReviewUpdatedFlagState
  );
  const setTrustBoxUpdateFlag = useSetRecoilState(trustBoxUpdatedFlagState);

  const userSearchOptions = useRecoilValue(userSearchOptionsSelector);
  const baseSearchOptions = useRecoilValue(baseSearchOptionsSelector);
  const loginUser = useRecoilValue(loginUserSelector);
  const statusList = useRecoilValue(statusListSelector);

  const {
    getListingsItems,
    listingsItems,
    setListingsItems,
    error: listingsItemsError,
    isLoading: isListingsItemsLoading,
  } = useApiGetListingsItems();
  const { checkSellerId } = useApiCheckSellerId();

  // 「検索」で同期が始まった場合、完了後に自動で検索し直す
  const searchAfterSyncRef = useRef(false);
  const {
    isSyncing: syncItemsStatus,
    isStartingSync: isSyncItemsLoading,
    startSync,
    awaitSync,
  } = useListingsSync({
    tenantId: loginUser?.tenantId,
    userId: loginUser?.id,
    enabled: open,
    onMessage: (message, severity) => showToast(message, severity),
    onCompleted: (status) => {
      const shouldSearch = searchAfterSyncRef.current;
      searchAfterSyncRef.current = false;
      // 取り込めた商品が無い場合は再検索しても同期が繰り返されるだけなので行わない
      if (
        shouldSearch &&
        status.result === "success" &&
        (status.importedCount ?? 0) > 0
      ) {
        handleFbaSearch();
      }
    },
  });

  // 商品情報（商品名・セット数・納品単位・納品数など）が編集不可か。
  // 一般ユーザーは、要確認ステータスかつ出品情報が取得済みのときのみ編集可。
  const isItemFieldDisabled =
    (statusId !== Status.NeedsReview &&
      isGeneralUser(loginUser?.permissionId)) ||
    (statusId === Status.NeedsReview &&
      isGeneralUser(loginUser?.permissionId) &&
      (!listingsItems || listingsItems === null));

  const [toastOpen, setToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastSeverity, setToastSeverity] = useState<ListingsSyncSeverity>(
    "success"
  );
  const handleCloseToast = () => setToastOpen(false);
  const showToast = (message: string, severity: ListingsSyncSeverity) => {
    setToastMessage(message);
    setToastSeverity(severity);
    setToastOpen(true);
  };

  const [selectedInputDate, setSelectedInputDate] = useState<Date | null>(null);
  const [selectedAmazonItemName, setSelectedAmazonItemName] =
    useState<string>("");
  const [selectedSetCount, setSelectedSetCount] = useState("");
  const [selectedDeliveryCount, setSelectedDeliveryCount] = useState("");
  const [deliveryUnit, setDeliveryUnit] = useState<string>("");
  const [selectedExpectedCount, setSelectedExpectedCount] = useState("");
  const [selectedShippedCount, setSelectedShippedCount] = useState("");
  const [selectedReceivedCount, setSelectedReceivedCount] = useState("");
  const [selectedExpiryDate, setSelectedExpiryDate] = useState<Date | null>(
    null
  );
  const [selectedStorageLocation, setSelectedStorageLocation] = useState<
    string | null
  >("");
  const [selectedAsin, setSelectedAsin] = useState("");
  const [selectedSku, setSelectedSku] = useState("");
  const [selectedFnsku, setSelectedFnsku] = useState("");
  const [selectedJan, setSelectedJan] = useState<string | null>(null);
  const [selectedStickerNum, setSelectedStickerNum] = useState<number | null>(
    null
  );
  const [selectedReturnNum, setSelectedReturnNum] = useState<number | null>(
    null
  );
  const [selectedDestructionNum, setSelectedDestructionNum] = useState<
    number | null
  >(null);
  const [selectedMemo, setSelectedMemo] = useState<string | null>(null);
  const [notes, setNotes] = useState<Note[]>([]);
  const [selectedBaseId, setSelectedBaseId] = useState<number | null>(null);
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [selectedStatus, setSelectedStauts] = useState<number>(1);

  // ダイアログオープン時の状態
  const [preInputDate, setPreInputDate] = useState<Date | null>(null);
  const [preAmazonItemName, setPreAmazonItemName] = useState("");
  const [preSetCount, setPreSetCount] = useState("");
  const [preDeliveryUnit, setPreDeliveryUnit] = useState<string>("");
  const [preDeliveryCount, setPreDeliveryCount] = useState("");
  const [preReceivedCount, setPreReceivedCount] = useState("");
  const [preJan, setPreJan] = useState<string | null>(null);
  const [preExpiryDate, setPreExpiryDate] = useState<Date | null>(null);
  const [preStickerNum, setPreStickerNum] = useState<number | null>(null);
  const [preReturnNum, setPreReturnNum] = useState<number | null>(null);
  const [preDestructionNum, setPreDestructionNum] = useState<number | null>(
    null
  );
  const [preMemo, setPreMemo] = useState<string | null>("");
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [dialogMsg, setDialogMsg] = useState("");

  const [rows, setRows] = useState<number[]>([]);
  const [rowDates, setRowDates] = useState<Record<number, Date | null>>({});
  const [productSetValues, setProductSetValues] = useState<
    Record<number, SetRowValues>
  >({});

  // FBA検索
  const [keyword, setKeyword] = useState("");
  const [syncDialog, setSyncDialog] = useState(false);
  const [fbaDialogOpen, setFbaDialogOpen] = useState(false);

  const resetForm = () => {
    setSelectedInputDate(null);
    setSelectedAmazonItemName("");
    setSelectedSetCount("");
    setSelectedDeliveryCount("");
    setDeliveryUnit("");
    setSelectedExpectedCount("");
    setSelectedShippedCount("");
    setSelectedReceivedCount("");
    setSelectedExpiryDate(null);
    setSelectedStorageLocation(null);
    setSelectedAsin("");
    setSelectedSku("");
    setSelectedFnsku("");
    setSelectedJan(null);
    setSelectedStickerNum(null);
    setSelectedReturnNum(null);
    setSelectedDestructionNum(null);
    setSelectedMemo(null);
    setNotes([]);
    setPreInputDate(null);
    setPreAmazonItemName("");
    setPreSetCount("");
    setPreDeliveryCount("");
    setPreDeliveryUnit("");
    setPreReceivedCount("");
    setPreExpiryDate(null);
    setPreStickerNum(null);
    setPreReturnNum(null);
    setPreDestructionNum(null);
    setPreMemo("");
    setListingsItems(null);
  };

  const preClose = () => {
    resetForm();
    onClose();
  };

  const closeCheck = () => {
    let msg = "";
    if (dateFormat2(selectedInputDate) !== dateFormat2(preInputDate))
      msg += "・入荷日\n";
    if (selectedAmazonItemName !== preAmazonItemName) msg += "・商品名\n";
    if (selectedSetCount !== preSetCount) msg += "・セット数\n";
    if (deliveryUnit !== preDeliveryUnit) msg += "・納品単位\n";
    if (selectedDeliveryCount !== preDeliveryCount) msg += "・納品数\n";
    if (selectedReceivedCount !== preReceivedCount) msg += "・実入荷総数\n";
    if (formatYMD(selectedExpiryDate) !== formatYMD(preExpiryDate))
      msg += "・賞味期限\n";
    if (selectedStickerNum !== preStickerNum) msg += "・シール\n";
    if (selectedReturnNum !== preReturnNum) msg += "・返送\n";
    if (selectedDestructionNum !== preDestructionNum) msg += "・破棄\n";
    const formattedNotes = notes
      .map((note) => {
        let base = `${note.timestamp} ${note.user}：${note.content}`;
        if (note.imageUrl) {
          base += ` | imageUrl=${note.imageUrl}`;
        }
        return base;
      })
      .join("\n");
    if (formattedNotes !== (preMemo ?? "")) msg += "・備考\n";
    if (msg.length > 0) {
      msg =
        "更新されていない項目があります。\n閉じてもよろしいですか？\n\n" + msg;
      setDialogMsg(msg);
      setConfirmDialogOpen(true);
    } else {
      preClose();
    }
  };

  const updateItemsAsync = async (req: RequestUpdateAmazonItemsRequest) => {
    const res = await updateAmazonItems(req);
    if (res) {
      setToastMessage("更新が完了しました");
      setToastSeverity("success");
      setToastOpen(true);
      setTimeout(() => {
        setToastOpen(false);
      }, 1500);
      switch (statusId) {
        case Status.PendingReceipt:
          setPendingReceiptUpdatedFlag(true);
          break;
        case Status.PendingPackage:
          setPendingPackagingUpdatedFlag(true);
          break;
        case Status.PendingPlan:
          setPendingPlanUpdatedFlag(true);
          break;
        case Status.NeedsReview:
          setNeedsReviewUpdateFlag(true);
          break;
        case Status.TrustBox:
          setTrustBoxUpdateFlag(true);
          break;
        default:
          break;
      }
      preClose();
    } else {
      setToastMessage("更新に失敗しました");
      setToastSeverity("error");
      setToastOpen(true);
    }
    // onClose();
  };

  const handleRegistItems = async () => {
    if (checkValidate()) return;
    // if (statusId === Status.TrustBox) {
    //   if (await checkRerstoreValidate()) {
    //     setToastMessage("既に存在しているFNSKUです");
    //     setToastSeverity("error");
    //     setToastOpen(true);
    //   }
    //   return;
    // }

    if (selectedBaseId === null || selectedUserId === null) return;
    if (!loginUser || !loginUser.tenantId || !loginUser.id) return;
    const id = Number(String(parentItem?.id).split("-")[1]);
    // セットがある場合
    if (rows.length > 0) {
      const setItems: RequestSetItem[] = rows.map((id) => {
        const values = productSetValues[id];
        const item: RequestSetItem = {
          jan: values?.jan ?? "",
          name: values?.name ?? "",
          setCount: Number(values?.setCount ?? 0),
          arriveCount: Number(values?.deliveryCount ?? 0), // 納品数
          expectedCount: Number(values?.expectedCount ?? 0), // 予定入荷総数
          actualShippingNum: Number(values.shippedCount ?? 0), // 実出荷単位数
          sumNum: Number(values.receivedCount ?? 0), // 実入荷総数
          place: values.storageLocation,
        };
        if (values.expiryDate) {
          item.expiryDate = new Date(values.expiryDate).toISOString();
        }

        return item;
      });
      const formattedMemo = notes
        .map((note) => {
          let base = `${note.timestamp} ${note.user}：${note.content}`;
          if (note.imageUrl) {
            base += ` | imageUrl=${note.imageUrl}`;
          }
          return base;
        })
        .join("\n");

      const req: RequestUpdateAmazonItemsRequest = {
        id: id,
        tenantId: loginUser?.tenantId,
        amazonItemName: selectedAmazonItemName, // 商品名
        setCount: Number(selectedSetCount ?? 0), // セット数
        arriveCount: Number(selectedDeliveryCount ?? 0), // 納品数
        deliveryUnit: deliveryUnit,
        expectedCount: Number(selectedExpectedCount ?? 0), // 予定入荷総数
        actualShippingNum: Number(selectedShippedCount ?? 0), // 実出荷単位数
        sumNum: Number(selectedReceivedCount ?? 0), // 実入荷総数
        asin: selectedAsin,
        fnsku: selectedFnsku,
        sku: selectedSku,
        stickerNum: Number(selectedStickerNum ?? 0),
        returnNum: Number(selectedReturnNum ?? 0),
        destructionNum: Number(selectedDestructionNum ?? 0),
        memo: formattedMemo === "" ? String(selectedMemo ?? "") : formattedMemo,
        status:
          statusId === Status.NeedsReview && isGeneralUser(loginUser?.permissionId)
            ? Status.WarehouseReview
            : selectedStatus,
        baseId: selectedBaseId,
        userId: selectedUserId,
        operatorUserId: loginUser?.id,
        setItems: setItems,
        isNeedConfimationItem: false,
      };
      if (selectedExpiryDate) {
        req.expiryDate = new Date(selectedExpiryDate).toISOString();
      }
      updateItemsAsync(req);
    }
    // セットがない場合
    else {
      // 仮のセットが必要のため
      const setItems: RequestSetItem[] = [
        {
          jan: String(selectedJan ?? ""),
          name: selectedAmazonItemName,
          arriveCount: Number(selectedDeliveryCount ?? 0),
          expectedCount: Number(selectedExpectedCount ?? 0),
          setCount: Number(selectedSetCount ?? 0),
          sumNum: Number(selectedReceivedCount ?? 0),
          actualShippingNum: Number(selectedShippedCount ?? 0),
          expiryDate: undefined,
          place: String(selectedStorageLocation ?? ""),
        },
      ];
      if (selectedExpiryDate) {
        setItems[0].expiryDate = new Date(selectedExpiryDate).toISOString();
      }

      const formattedMemo = notes
        .map((note) => {
          let base = `${note.timestamp} ${note.user}：${note.content}`;
          if (note.imageUrl) {
            base += ` | imageUrl=${note.imageUrl}`;
          }
          return base;
        })
        .join("\n");

      const req: RequestUpdateAmazonItemsRequest = {
        id: id,
        tenantId: loginUser?.tenantId,
        amazonItemName: selectedAmazonItemName,
        setCount: Number(selectedSetCount ?? 0),
        arriveCount: Number(selectedDeliveryCount ?? 0), // 納品数
        deliveryUnit: deliveryUnit,
        expectedCount: Number(selectedExpectedCount ?? 0), // 予定入荷総数
        actualShippingNum: Number(selectedShippedCount ?? 0), // 実出荷単位数
        sumNum: Number(selectedReceivedCount ?? 0), // 実入荷総数
        storageLocation: String(selectedStorageLocation ?? ""),
        asin: selectedAsin,
        fnsku: selectedFnsku,
        sku: selectedSku,
        jan: String(selectedJan ?? ""),
        stickerNum: Number(selectedStickerNum ?? 0),
        returnNum: Number(selectedReturnNum ?? 0),
        destructionNum: Number(selectedDestructionNum ?? 0),
        memo: formattedMemo === "" ? String(selectedMemo ?? "") : formattedMemo,
        status:
          statusId === Status.NeedsReview && isGeneralUser(loginUser?.permissionId)
            ? Status.WarehouseReview
            : selectedStatus,
        baseId: selectedBaseId,
        userId: selectedUserId,
        operatorUserId: loginUser?.id,
        setItems: setItems,
        isNeedConfimationItem: false,
      };
      if (selectedExpiryDate) {
        req.expiryDate = new Date(selectedExpiryDate).toISOString();
      }
      updateItemsAsync(req);
    }

    // 要確認に変更する場合はチャットワークと連携する
    if (selectedStatus === Status.NeedsReview) {
      const chatworkReq: RequestChatworkSendMessageRequest = {
        createdUserId: loginUser.id,
        userId: Number(selectedUserId ?? 0),
        message: `商品名：『${selectedAmazonItemName}』が要確認リストに追加されました。ご確認よろしくお願いいたします。`,
      };
      sendChatwoekMessage(chatworkReq);
    }
  };

  // ダイアログ開いた時の初期値設定
  // 入力日
  useEffect(() => {
    if (parentItem?.inputDate) {
      setSelectedInputDate(new Date(parentItem.inputDate));
      setPreInputDate(new Date(parentItem.inputDate));
    }
  }, [parentItem?.inputDate]);
  // 商品名
  useEffect(() => {
    if (parentItem?.itemName) {
      setSelectedAmazonItemName(parentItem.itemName);
      setPreAmazonItemName(parentItem.itemName);
    }
  }, [parentItem?.itemName]);
  // セット数
  useEffect(() => {
    if (parentItem?.setCount) {
      setSelectedSetCount(String(parentItem.setCount));
      setPreSetCount(String(parentItem.setCount));
    }
  }, [parentItem?.setCount]);
  // 納品数
  useEffect(() => {
    if (parentItem?.deliveryCount) {
      setSelectedDeliveryCount(String(parentItem.deliveryCount));
      setPreDeliveryCount(String(parentItem.deliveryCount));
    }
  }, [parentItem?.deliveryCount]);
  // 納品単位
  useEffect(() => {
    if (parentItem?.deliveryUnit) {
      setDeliveryUnit(String(parentItem.deliveryUnit));
      setPreDeliveryUnit(String(parentItem.deliveryUnit));
    }
  }, [parentItem?.deliveryUnit]);
  // 予定入荷総数
  useEffect(() => {
    if (parentItem?.expectedCount) {
      setSelectedExpectedCount(String(parentItem.expectedCount));
    }
  }, [parentItem?.expectedCount]);
  // 実出荷単位数
  useEffect(() => {
    if (parentItem?.shippedCount) {
      setSelectedShippedCount(String(parentItem.shippedCount));
    }
  }, [parentItem?.shippedCount]);
  // 実入荷総数
  useEffect(() => {
    if (parentItem?.receivedCount) {
      setSelectedReceivedCount(String(parentItem.receivedCount));
      setPreReceivedCount(String(parentItem.receivedCount));
    }
  }, [parentItem?.receivedCount]);
  // 賞味期限
  useEffect(() => {
    if (parentItem?.expiryDate) {
      setSelectedExpiryDate(new Date(parentItem.expiryDate));
      setPreExpiryDate(new Date(parentItem.expiryDate));
    }
  }, [parentItem?.expiryDate]);
  // 保管場所
  useEffect(() => {
    if (parentItem?.storageLocation) {
      setSelectedStorageLocation(parentItem.storageLocation);
    }
  }, [parentItem?.storageLocation]);
  // ASIN
  useEffect(() => {
    if (parentItem?.asin) {
      setSelectedAsin(parentItem.asin ?? "");
    }
  }, [parentItem?.asin]);
  // SKU
  useEffect(() => {
    if (parentItem?.sku) {
      setSelectedSku(parentItem.sku ?? "");
    }
  }, [parentItem?.sku]);
  // FNSKU
  useEffect(() => {
    if (parentItem?.fnsku) {
      setSelectedFnsku(parentItem.fnsku ?? "");
    }
  }, [parentItem?.fnsku]);
  // JAN
  useEffect(() => {
    if (parentItem?.janCode && /^\d+$/.test(parentItem.janCode)) {
      setSelectedJan(parentItem.janCode);
    } else {
      setSelectedJan(null);
    }
  }, [parentItem?.janCode]);
  // シール
  useEffect(() => {
    if (parentItem?.stickerNum) {
      setSelectedStickerNum(parentItem.stickerNum);
      setPreStickerNum(parentItem.stickerNum);
    }
  }, [parentItem?.stickerNum]);
  // 返送
  useEffect(() => {
    if (parentItem?.returnNum) {
      setSelectedReturnNum(parentItem.returnNum);
      setPreReturnNum(parentItem.returnNum);
    }
  }, [parentItem?.returnNum]);
  // 破棄
  useEffect(() => {
    if (parentItem?.destructionNum) {
      setSelectedDestructionNum(parentItem.destructionNum);
      setPreDestructionNum(parentItem.destructionNum);
    }
  }, [parentItem?.destructionNum]);
  // 拠点
  useEffect(() => {
    if (parentItem?.baseId) {
      setSelectedBaseId(parentItem.baseId);
    }
  }, [parentItem?.baseId]);
  // ユーザー
  useEffect(() => {
    if (parentItem?.userId) {
      setSelectedUserId(parentItem.userId);
    }
  }, [parentItem?.userId]);
  // ステータス
  useEffect(() => {
    if (parentItem?.statusId) {
      setSelectedStauts(
        parentItem?.statusId > 0 && parentItem?.statusId < 8
          ? parentItem.statusId
          : -1
      );
    }
  }, [parentItem?.statusId]);
  // 備考
  useEffect(() => {
    if (parentItem?.memo) {
      setSelectedMemo(parentItem.memo);
      // notes と preMemo を同じレンダーで設定することで、
      // 「開いてすぐ閉じる」時のレースコンディションを防ぐ。
      // パース失敗時（備考内容に改行を含む場合など）は preMemo を "" に
      // して formattedNotes（=""）と一致させ、誤アラートを防ぐ。
      try {
        const parsedNotes: Note[] = parentItem.memo.split("\n").map((line) => {
          const match = line.match(
            /^(\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}) (.+?)：(.+?)(?: \| imageUrl=(.+))?$/
          );
          if (!match) throw new Error(`Invalid format: ${line}`);
          return {
            timestamp: match[1],
            user: match[2],
            content: match[3],
            imageUrl: match[4] || undefined,
          };
        });
        setNotes(parsedNotes);
        setPreMemo(parentItem.memo);
      } catch {
        setPreMemo("");
      }
    }
  }, [parentItem?.memo]);

  useEffect(() => {
    if (childItems && childItems.length > 0) {
      const newRows = childItems.map((_, index) => index + 1);
      const newValues: Record<number, SetRowValues> = {};

      childItems.forEach((child, index) => {
        const id = index + 1;
        newValues[id] = {
          jan: child.janCode ?? "",
          name: child.itemName ?? "",
          setCount: String(child.setCount ?? ""),
          deliveryCount: String(child.deliveryCount ?? ""),
          expectedCount: String(child.expectedCount ?? ""),
          shippedCount: String(child.shippedCount ?? ""),
          receivedCount: String(child.receivedCount ?? ""),
          expiryDate: child.expiryDate ? new Date(child.expiryDate) : null,
          storageLocation: child.storageLocation ?? "",
        };
      });

      setRows(newRows);
      setProductSetValues(newValues);
    } else {
      // 子がいない場合は初期化してもよい
      setRows([]);
      setProductSetValues({});
    }
  }, [childItems]);

  const userOptions = useMemo(() => {
    return (
      userSearchOptions?.map((user) => ({
        label: `${user.userNumber ?? ""}_${user.lastName ?? ""}${user.firstName ?? ""}`,
        id: user.id!,
      })) ?? []
    );
  }, [userSearchOptions]);

  const baseOptions = useMemo(() => {
    return (
      baseSearchOptions?.map((base) => ({
        label: `${base.name}`,
        id: base.id!,
      })) ?? []
    );
  }, [baseSearchOptions]);

  useEffect(() => {
    if (rows.length > 0) {
      const updated: Record<number, SetRowValues> = {};
      rows.forEach((id) => {
        updated[id] = {
          ...productSetValues[id],
          deliveryCount: selectedDeliveryCount,
        };
      });
      setProductSetValues(updated);
    }
  }, [selectedDeliveryCount, rows]);

  const handleUserChange = (userId: number | null) => {
    setSelectedUserId(userId);
    // ここでフィルタ処理やAPI呼び出し
  };

  const handleBaseChange = (baseId: number | null) => {
    setSelectedBaseId(baseId);
    // ここでフィルタ処理やAPI呼び出し
  };

  const handleStatusChange = (id: number) => {
    setSelectedStauts(id);
  };

  const handleAddRow = () => {
    if (rows.length === 0) {
      const newId1 = 1;
      const newId2 = 2;

      setRows([newId1, newId2]);
      setProductSetValues({
        [newId1]: {
          jan: String(selectedJan ?? ""),
          name: selectedAmazonItemName,
          setCount: "",
          deliveryCount: String(selectedDeliveryCount ?? ""),
          expectedCount: String(selectedExpectedCount ?? ""),
          shippedCount: String(selectedShippedCount ?? ""),
          receivedCount: String(selectedReceivedCount ?? ""),
          expiryDate: selectedExpiryDate,
          storageLocation: selectedStorageLocation ?? "",
        },
        [newId2]: {
          jan: "",
          name: "",
          setCount: "",
          deliveryCount: selectedDeliveryCount,
          expectedCount: "",
          shippedCount: "",
          receivedCount: "",
          expiryDate: null,
          storageLocation: "",
        },
      });
    } else {
      const nextId = Math.max(...rows) + 1;
      setRows((prev) => [...prev, nextId]);
      setProductSetValues((prev) => ({
        ...prev,
        [nextId]: {
          jan: "",
          name: "",
          setCount: "",
          deliveryCount: selectedDeliveryCount,
          expectedCount: "",
          shippedCount: "",
          receivedCount: "",
          expiryDate: null,
          storageLocation: "",
        },
      }));
      if (rows.length + 1 > Number(selectedSetCount)) {
        setSelectedSetCount(String(rows.length + 1));
      }
    }
  };

  const handleRowDateChange = (id: number, date: Date | null) => {
    setRowDates((prev) => ({ ...prev, [id]: date }));
  };

  const handleDeleteRow = (id: number) => {
    setRows((prev) => {
      if (prev.length <= 2) {
        const updated = prev.filter((rowId) => rowId !== id);
        const remainingId = updated[0];
        const values = productSetValues[remainingId];
        if (values) {
          const _id = id === 1 ? 2 : 1;
          setSelectedJan(productSetValues[_id].jan);
          setSelectedAmazonItemName(values.name ?? "");
          setSelectedSetCount(preSetCount);
          setSelectedDeliveryCount(values.deliveryCount ?? "");
          // setSelectedExpectedCount(values.expectedCount ?? "");
          setSelectedShippedCount(values.shippedCount ?? "");
          setSelectedReceivedCount(values.receivedCount ?? "");
          setSelectedExpiryDate(values.expiryDate ?? null);
          setSelectedStorageLocation(values.storageLocation ?? "");
        }

        setRowDates({});
        return [];
      } else {
        const updated = prev.filter((rowId) => rowId !== id);
        const newDates = { ...rowDates };
        delete newDates[id];
        setRowDates(newDates);
        if (prev.length - 1 < Number(selectedSetCount)) {
          setSelectedSetCount(String(prev.length - 1));
        }
        return updated;
      }
    });
  };

  const handleProductSetValueChange = (id: number, newValues: SetRowValues) => {
    const setCount = Number(newValues.setCount);
    const deliveryCount = Number(newValues.deliveryCount);
    const expectedCount =
      !isNaN(setCount) && !isNaN(deliveryCount) ? setCount * deliveryCount : "";

    const updatedValues: SetRowValues = {
      ...newValues,
      expectedCount: expectedCount.toString(),
    };

    setProductSetValues((prev) => ({
      ...prev,
      [id]: updatedValues,
    }));
  };

  const handlePrintLabel = () => {
    if (selectedReceivedCount !== selectedExpectedCount) {
      setToastMessage("予定入荷総数と実入荷総数が一致していません");
      setToastSeverity("error");
      setToastOpen(true);
      setTimeout(() => {
        setToastOpen(false);
      }, 1500);
      return;
    }
    const fnsku = toDisplayFnsku(selectedFnsku);
    const itemName = selectedAmazonItemName;
    const count = Number(selectedDeliveryCount || 1);

    const url = `/printLabel?fnsku=${encodeURIComponent(
      fnsku
    )}&itemName=${encodeURIComponent(itemName)}&count=${count}`;
    const printWindow = window.open(url, "_blank");

    if (!printWindow) {
      alert("ポップアップブロックにより印刷ウィンドウを開けませんでした。");
    }
  };

  const checkRerstoreValidate = async (): Promise<boolean> => {
    // 復元可能かチェック
    const fetchData = async (req: RequestGetAmazonItemsListRequest) => {
      const response = await fetchAmazonItems(req);
      if (!response || !response.items) {
        return false;
      }
      // fnsku_code は登録ごとにサフィックスが付くため、素の FNSKU 同士で比較する
      const targetFnsku = toDisplayFnsku(selectedFnsku);
      return response.items.some(
        (item) =>
          toDisplayFnsku(item.fnskuCode) === targetFnsku && item.statusId !== 5
      );
    };

    if (loginUser && loginUser.tenantId && loginUser.id) {
      const req: RequestGetAmazonItemsListRequest = {
        tenantId: loginUser.tenantId,
        userId: loginUser.id,
        pageSize: 10000,
      };
      return await fetchData(req);
    }

    return false; // ログインユーザーが不正なら false を返す
  };

  const checkValidate = () => {
    const isEmpty = (v: string | undefined | null) => !v || v.trim() === "";
    let msg = "";
    let selectMsg = "";
    let errMsg = "";
    // 納品単位が編集不可のユーザーには必須チェックを課さない。
    // 未入力のまま編集もできない状態だと、備考など他項目の更新までブロックされてしまうため。
    const isDeliveryUnitMissing = !isItemFieldDisabled && isEmpty(deliveryUnit);

    if (isEmpty(selectedAmazonItemName)) msg += "・商品名\n";
    if (isEmpty(selectedDeliveryCount)) msg += "・納品数\n";
    if (isDeliveryUnitMissing) msg += "・納品単位\n";
    if (isEmpty(selectedAsin)) msg += "・ASIN\n";
    if (isEmpty(selectedSku)) msg += "・SKU\n";
    if (isEmpty(selectedFnsku)) msg += "・FNSKU\n";
    if (selectedBaseId === null) selectMsg += "・拠点\n";
    if (selectedUserId === null) selectMsg += "・ユーザー\n";
    if (selectedStatus === -1) selectMsg += "ステータス\n";

    const commonInvalid =
      isEmpty(selectedAmazonItemName) ||
      isDeliveryUnitMissing ||
      isEmpty(selectedAsin) ||
      isEmpty(selectedSku) ||
      isEmpty(selectedFnsku) ||
      selectedBaseId === null ||
      selectedUserId === null ||
      selectedStatus === -1;

    if (Number(selectedSetCount) === 0 || selectedSetCount === "") {
      errMsg += "<br />セット数に1以上を入力してください";
    }

    if (rows.length === 0) {
      // セットがない（単品登録）
      if (isEmpty(selectedJan)) msg += "・JAN\n";
      if (isEmpty(selectedSetCount)) msg += "・セット数\n";

      if (
        commonInvalid ||
        isEmpty(selectedJan) ||
        isEmpty(selectedSetCount) ||
        isEmpty(selectedDeliveryCount) ||
        Number(selectedSetCount) === 0 ||
        selectedSetCount === ""
      ) {
        // トースト表示用
        if (msg.length > 0)
          msg =
            "以下の項目が未入力です。<br />" +
            msg.replace(/\n/g, "<br />") +
            "<br />";
        if (selectMsg.length > 0)
          selectMsg =
            "以下の項目が未選択です。<br />" +
            selectMsg.replace(/\n/g, "<br />");
        setToastMessage(msg + selectMsg + errMsg);
        setToastSeverity("error");
        setToastOpen(true);
        return true;
      }

      return false;
    }

    // セットがある場合
    let janMissing = false;
    let nameMissing = false;
    let setCountMissing = false;
    let expectedCountMissing = false;
    let totalChileSetCount = 0;
    for (const id of rows) {
      const v = productSetValues[id];
      if (!v) continue;

      if (isEmpty(v.jan)) janMissing = true;
      if (isEmpty(v.name)) nameMissing = true;
      if (v.setCount === "") setCountMissing = true;
      // if (v.deliveryCount === "") deliveryCountMissing = true;
      if (v.expectedCount === "") expectedCountMissing = true;
      totalChileSetCount += Number(v.setCount);
    }

    if (janMissing) msg += "・JAN（子）\n";
    if (nameMissing) msg += "・商品名（子）\n";
    if (setCountMissing) msg += "・セット数（子）\n";
    if (expectedCountMissing) msg += "・予定入荷総数（子）\n";

    // 親のセット数と子の数チェック
    if (Number(selectedSetCount) !== totalChileSetCount) {
      errMsg += "<br />親のセット数と子のセット数が異なります。";
    }

    if (
      commonInvalid ||
      janMissing ||
      nameMissing ||
      setCountMissing ||
      expectedCountMissing ||
      Number(selectedSetCount) !== totalChileSetCount
    ) {
      // トースト表示用
      if (msg.length > 0)
        msg =
          "以下の項目が未入力です。<br />" +
          msg.replace(/\n/g, "<br />") +
          "<br />";
      if (selectMsg.length > 0)
        selectMsg =
          "以下の項目が未選択です。<br />" + selectMsg.replace(/\n/g, "<br />");
      setToastMessage(msg + selectMsg + errMsg);
      setToastSeverity("error");
      setToastOpen(true);
      return true;
    }

    return false;
  };

  const handleFbaSearch = async () => {
    if (!loginUser?.id) return;
    const reqSellerId: RequestCheckSellerIdRequest = {
      userId: loginUser.id,
    };
    const IsSellerId = await checkSellerId(reqSellerId);
    if (!IsSellerId) {
      setToastMessage(
        "セラーIDが設定されていません。ユーザー情報ページから設定してください。"
      );
      setToastSeverity("error");
      setToastOpen(true);
      return;
    }
    const req: RequestGetListingsItemsRequest = {
      userId: loginUser.id,
      tenantId: loginUser.tenantId,
    };
    const result = await getListingsItems(req);
    if (result?.syncStarted) {
      // 商品情報が未取得のため同期が始まった。完了後に自動で検索結果を表示する
      searchAfterSyncRef.current = true;
      showToast(
        "商品情報を取得するため、セラーセントラルとの同期を開始しました。<br />完了すると自動で検索結果を表示します。",
        "info"
      );
      awaitSync();
    }
  };

  const handleSelectListingsItem = (item: ResponseListingsItemResponse) => {
    for (let i = rows.length; i > 0; i--) {
      handleDeleteRow(rows.length);
    }
    setFbaDialogOpen(false);
    setSelectedAmazonItemName(item.itemName ?? "");
    setSelectedSetCount(String(item.packageQuantity) ?? "1");
    // setDeliveryCount(String(item.packageQuantity) ?? "0");
    setSelectedAsin(item.asin ?? "");
    setSelectedSku(item.sku ?? "");
    setSelectedFnsku(item.fnsku ?? "");
    if (item.jan?.length === 0 || item.jan?.length === 1) {
      if (item.jan) {
        setSelectedJan(item.jan?.length === 0 ? "" : item.jan[0] ?? "");
      }
    } else {
      const janList = item.jan ?? [];

      // janの数だけ行追加
      for (let i = 0; i < janList.length; i++) {
        handleAddRow();
      }
      setTimeout(() => {
        setProductSetValues((prev) => {
          const updated = { ...prev };
          let index = 0;
          for (const id of Object.keys(updated).map(Number)) {
            updated[id] = {
              ...updated[id],
              jan: janList[index] ?? "",
              name: item.itemName ?? "",
              setCount: "1",
              // deliveryCount: String(item.packageQuantity ?? "1"),
              // expectedCount: String(item.packageQuantity ?? "1"),
              expiryDate: null,
              storageLocation: "",
            };
            index++;
          }
          return updated;
        });
      }, 100);
    }
  };

  const syncItems = async () => {
    searchAfterSyncRef.current = false;
    await startSync();
  };

  useEffect(() => {
    if (!listingsItems || listingsItems === null) {
      setSelectedAmazonItemName("");
      setSelectedSetCount("1");
      setSelectedDeliveryCount("");
      setDeliveryUnit("");
      setSelectedExpiryDate(null);
      setSelectedAsin("");
      setSelectedSku("");
      setSelectedFnsku("");
      setSelectedJan("");
      return;
    }
    setFbaDialogOpen(true);
  }, [listingsItems]);

  return (
    <>
      <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ja}>
        <ConfirmDialog
          open={confirmDialogOpen}
          title="確認"
          message={dialogMsg}
          buttons={[
            {
              label: "いいえ",
              variant: "outlined",
              onClick: () => {
                setConfirmDialogOpen(false);
              },
            },
            {
              label: "はい",
              variant: "contained",
              onClick: () => {
                setConfirmDialogOpen(false);
                preClose();
              },
            },
          ]}
        />
        <ListingsItemDialog
          open={fbaDialogOpen}
          onClose={() => setFbaDialogOpen(false)}
          listingsItems={listingsItems!}
          setListingsItems={setListingsItems}
          onSelect={handleSelectListingsItem}
          initialKeyword={keyword}
        />
        <ConfirmDialog
          open={syncDialog}
          title="確認"
          message={
            "同期中は商品情報の検索、CSVアップロードができなくなります。\nよろしいですか？\n\n※商品を追加した場合は、「検索」押下で追加可能です。\n※商品情報の変更、削除を行った場合に実施してください。\n※商品数が多いと同期時間も長くなります。"
          }
          buttons={[
            {
              label: "いいえ",
              variant: "text",
              onClick: () => setSyncDialog(false),
            },
            {
              label: "はい",
              variant: "contained",
              onClick: () => {
                syncItems();
                setSyncDialog(false);
              },
            },
          ]}
        />

        <Dialog
          open={open}
          onClose={(event, reason) => {
            if (reason !== "backdropClick") {
              onClose(); // backdropClick以外の時だけ閉じる
            }
          }}
          disableEnforceFocus
          maxWidth="xl"
          fullWidth
        >
          <Box sx={styles.buttonWrapper}>
            <Box display="flex" gap={2} mr={2}>
              <Button
                sx={styles.leftButton}
                onClick={() =>
                  window.open(
                    `https://www.amazon.co.jp/dp/${parentItem!.asin}`,
                    "_blank"
                  )
                }
              >
                Amazonページ
              </Button>
              {/* <Button sx={styles.rightButton}>JAN更新</Button> */}
              <Box sx={{ width: "120px" }} />
            </Box>
            <Box sx={styles.titleBar}>
              <Typography variant="h6" sx={styles.titleText}>
                商品情報
              </Typography>
            </Box>
            <Box display="flex" gap={1} ml={2}>
              {statusId === 1 ? (
                // <Button sx={styles.rightButton}>入荷更新ST</Button>
                <Box sx={{ width: "120px" }} />
              ) : (
                <Box sx={{ width: "120px" }} />
              )}
              {statusId === Status.TrustBox ? (
                <>
                  <Box sx={{ width: "120px" }} />
                  <Box sx={{ width: "120px" }} />
                  {/* <Button sx={styles.rightButton} onClick={handleRestore}>
                    復元
                  </Button> */}
                </>
              ) : (
                <>
                  {/* <Button sx={styles.rightButton}>更新</Button> */}
                  <Box sx={{ width: "120px" }} />
                  {isNotGeneralUser(loginUser?.permissionId) ? (
                    <Button sx={styles.rightButton} onClick={handlePrintLabel}>
                      ラベル発行
                    </Button>
                  ) : (
                    <Box sx={{ width: "120px" }} />
                  )}
                </>
              )}

              <Button onClick={closeCheck} sx={styles.closeButton}>
                ×
              </Button>
            </Box>
            <DialogActions sx={{ position: "absolute", top: 8, right: 8 }}>
              {!hideCancel && (
                <Button onClick={preClose} color="secondary" size="small">
                  {cancelText}
                </Button>
              )}
            </DialogActions>
          </Box>
          {statusId === Status.NeedsReview && (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              gap={1}
              mb={1}
              px={2}
            >
              <Box sx={styles.fbaItem}>
                <TextField
                  label={
                    "登録する商品のキーワード、JANコード、ASINなどを入力してください"
                  }
                  size="small"
                  sx={styles.keywordInput}
                  InputLabelProps={{ sx: { fontSize: "0.9rem" } }}
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  disabled={isListingsItemsLoading}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleFbaSearch();
                    }
                  }}
                  InputProps={{
                    endAdornment: keyword && (
                      <IconButton size="small" onClick={() => setKeyword("")}>
                        <ClearIcon />
                      </IconButton>
                    ),
                  }}
                />
                <Tooltip title="初回検索時は時間がかかります" arrow>
                  <Button
                    variant="contained"
                    sx={styles.searchButton}
                    onClick={() => handleFbaSearch()}
                    disabled={
                      isListingsItemsLoading ||
                      isSyncItemsLoading ||
                      syncItemsStatus
                    }
                  >
                    {syncItemsStatus ? "同期中" : "検索"}
                  </Button>
                </Tooltip>
                <Button
                  variant="contained"
                  sx={styles.syncButton}
                  onClick={() => setSyncDialog(true)}
                  disabled={
                    isListingsItemsLoading ||
                    isSyncItemsLoading ||
                    syncItemsStatus
                  }
                >
                  {syncItemsStatus ? "同期中" : "同期"}
                </Button>
              </Box>
            </Box>
          )}
          <Box display="flex" alignItems="center" gap={1} mb={2} px={2}>
            <DatePicker
              label="入荷日"
              value={selectedInputDate}
              onChange={(newValue) => setSelectedInputDate(newValue)}
              // minDate={new Date()}
              format="yyyy/MM/dd"
              slotProps={{
                textField: {
                  size: "small",
                  inputProps: {
                    readOnly: false,
                  },
                  InputLabelProps: {
                    sx: {
                      fontSize: "0.9rem",
                    },
                  },
                },
              }}
              sx={{ width: "11%" }}
              disabled={
                statusId !== Status.NeedsReview && isGeneralUser(loginUser?.permissionId)
              }
            />
            <TextField
              label="商品名"
              variant="outlined"
              size="small"
              sx={{ width: "35%" }}
              InputLabelProps={{
                sx: {
                  fontSize: "0.9rem",
                },
              }}
              value={selectedAmazonItemName}
              onChange={(e) => setSelectedAmazonItemName(e.target.value)}
              disabled={isItemFieldDisabled}
            />
            <TextField
              label={"セット数"}
              size="small"
              sx={{ width: "5%" }}
              InputLabelProps={{
                sx: {
                  fontSize: "0.9rem",
                },
              }}
              value={selectedSetCount}
              onChange={(e) => {
                const inputValue = e.target.value;
                if (/^\d*$/.test(inputValue)) {
                  setSelectedSetCount(inputValue);
                }
              }}
              disabled={isItemFieldDisabled}
            />
            <DeliveryUnitSelect
              value={deliveryUnit}
              onChange={setDeliveryUnit}
              sx={{ width: "15%" }}
              isDisabled={isItemFieldDisabled}
            />
            <TextField
              label={"納品数"}
              size="small"
              sx={{ width: "5%" }}
              InputLabelProps={{
                sx: {
                  fontSize: "0.9rem",
                },
              }}
              value={selectedDeliveryCount}
              onChange={(e) => {
                const inputValue = e.target.value;
                if (/^\d*$/.test(inputValue)) {
                  setSelectedDeliveryCount(inputValue);
                }
              }}
              disabled={isItemFieldDisabled}
            />
            <TextField
              label={"予定入荷総数"}
              size="small"
              sx={{
                width: "8%",
                backgroundColor: "#a0a0a0",
              }}
              InputLabelProps={{
                sx: {
                  fontSize: "0.9rem",
                },
              }}
              value={String(
                Number(selectedSetCount) * Number(selectedDeliveryCount)
              )}
              onChange={(e) => {
                const inputValue = e.target.value;
                if (/^\d*$/.test(inputValue)) {
                  setSelectedExpectedCount(inputValue);
                }
              }}
              disabled
            />
            <TextField
              label={"実出荷単位数"}
              size="small"
              sx={{
                width: "8%",
                backgroundColor: "#a0a0a0",
              }}
              InputLabelProps={{
                sx: {
                  fontSize: "0.9rem",
                },
              }}
              value={String(
                Math.round(
                  Number(selectedReceivedCount) / Number(selectedSetCount)
                )
              )} //{selectedShippedCount}
              // onChange={(e) => {
              //   const inputValue = e.target.value;
              //   if (/^\d*$/.test(inputValue)) {
              //     setSelectedShippedCount(inputValue);
              //   }
              // }}
              disabled
            />
            <TextField
              label={"実入荷総数"}
              size="small"
              sx={{
                width: "8%",
              }}
              InputLabelProps={{
                sx: {
                  fontSize: "0.9rem",
                },
              }}
              value={selectedReceivedCount}
              onChange={(e) => {
                const inputValue = e.target.value;
                if (/^\d*$/.test(inputValue)) {
                  setSelectedReceivedCount(inputValue);
                }
              }}
              disabled={isGeneralUser(loginUser?.permissionId)}
            />
            <DatePicker
              label="賞味期限"
              value={selectedExpiryDate}
              onChange={(newValue) => setSelectedExpiryDate(newValue)}
              minDate={new Date()}
              format="yyyy/MM/dd"
              slotProps={{
                textField: {
                  size: "small",
                  inputProps: {
                    readOnly: false,
                  },
                  InputLabelProps: {
                    sx: {
                      fontSize: "0.9rem",
                    },
                  },
                },
              }}
              sx={{
                width: "11%",
              }}
              disabled={isItemFieldDisabled}
            />
            {/* <TextField
              label={"保管場所"}
              size="small"
              sx={{
                width: "15%",
                backgroundColor: rows.length > 0 ? "#a0a0a0" : "inherit",
              }}
              InputLabelProps={{
                sx: {
                  fontSize: "0.9rem",
                },
              }}
              value={selectedStorageLocation}
              onChange={(e) => setSelectedStorageLocation(e.target.value)}
              disabled={rows.length > 0}
            /> */}
          </Box>
          <Box display="flex" alignItems="center" gap={1} mb={2} px={2}>
            <TextField
              label={"ASIN"}
              size="small"
              sx={{
                width: "10%",
                backgroundColor:
                  isNotOwner(loginUser?.permissionId) ? "#a0a0a0" : "inherit",
              }}
              InputLabelProps={{
                sx: {
                  fontSize: "0.9rem",
                },
              }}
              value={selectedAsin}
              onChange={(e) => setSelectedAsin(e.target.value)}
              disabled={isNotOwner(loginUser?.permissionId)}
            />
            <TextField
              label={"SKU"}
              size="small"
              sx={{
                width: "10%",
                backgroundColor:
                  isNotOwner(loginUser?.permissionId) ? "#a0a0a0" : "inherit",
              }}
              InputLabelProps={{
                sx: {
                  fontSize: "0.9rem",
                },
              }}
              value={selectedSku}
              onChange={(e) => setSelectedSku(e.target.value)}
              disabled={isNotOwner(loginUser?.permissionId)}
            />
            <TextField
              label={"FNSKU"}
              size="small"
              sx={{
                width: "10%",
                backgroundColor:
                  isNotOwner(loginUser?.permissionId) ? "#a0a0a0" : "inherit",
              }}
              InputLabelProps={{
                sx: {
                  fontSize: "0.9rem",
                },
              }}
              // 画面にはサフィックスを出さず、編集時は元のサフィックスを引き継ぐ
              value={toDisplayFnsku(selectedFnsku)}
              onChange={(e) =>
                setSelectedFnsku(
                  withFnskuSuffixOf(e.target.value, selectedFnsku)
                )
              }
              disabled={isNotOwner(loginUser?.permissionId)}
            />
            <TextField
              label={"JAN"}
              size="small"
              sx={{
                width: "12%",
                backgroundColor: rows.length > 0 ? "#a0a0a0" : "inherit",
              }}
              InputLabelProps={{
                sx: {
                  fontSize: "0.9rem",
                },
              }}
              value={rows.length > 0 ? null : selectedJan}
              onChange={(e) => setSelectedJan(e.target.value)}
              disabled={rows.length > 0 || isItemFieldDisabled}
            />
            <TextField
              label={"シール"}
              size="small"
              sx={{
                width: "5%",
              }}
              InputLabelProps={{
                sx: {
                  fontSize: "0.9rem",
                },
              }}
              value={selectedStickerNum}
              onChange={(e) => {
                const inputValue = e.target.value;
                if (/^\d*$/.test(inputValue)) {
                  setSelectedStickerNum(Number(inputValue));
                }
              }}
              disabled={isGeneralUser(loginUser?.permissionId)}
            />
            <TextField
              label={"返送"}
              size="small"
              sx={{
                width: "5%",
              }}
              InputLabelProps={{
                sx: {
                  fontSize: "0.9rem",
                },
              }}
              value={selectedReturnNum}
              onChange={(e) => {
                const inputValue = e.target.value;
                if (/^\d*$/.test(inputValue)) {
                  setSelectedReturnNum(Number(inputValue));
                }
              }}
              disabled={isGeneralUser(loginUser?.permissionId)}
            />
            <TextField
              label={"破棄"}
              size="small"
              sx={{
                width: "5%",
              }}
              InputLabelProps={{
                sx: {
                  fontSize: "0.9rem",
                },
              }}
              value={selectedDestructionNum}
              onChange={(e) => {
                const inputValue = e.target.value;
                if (/^\d*$/.test(inputValue)) {
                  setSelectedDestructionNum(Number(inputValue));
                }
              }}
              disabled={isGeneralUser(loginUser?.permissionId)}
            />
            <BaseFilterSelect
              options={baseOptions}
              onSelect={handleBaseChange}
              sx={{ width: "14%" }}
              labelFontSize="0.9rem"
              value={selectedBaseId}
              isDisabled={isNotOwner(loginUser?.permissionId)} // {loginUser?.permissionId === 4}
            />
            <UserFilterSelect
              options={userOptions}
              onSelect={handleUserChange}
              sx={{ width: "14%" }}
              labelFontSize="0.9rem"
              value={selectedUserId}
              isDisabled={isNotOwner(loginUser?.permissionId)} // {loginUser?.permissionId === 4}
            />
            <StatusSelect
              statusId={statusId}
              options={statusList ?? []}
              initialValue={statusId ?? 1}
              onChange={handleStatusChange}
              isDisabled={isGeneralUser(loginUser?.permissionId)}
            />
          </Box>

          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            gap={1}
            mt={2}
            mb={1}
            px={2}
          >
            <Box>
              <Typography>商品一覧</Typography>
            </Box>
          </Box>
          {rows.map((id) => (
            <ProductSetRow
              key={id}
              id={id}
              values={
                productSetValues[id] ?? {
                  jan: "",
                  name: "",
                  setCount: "",
                  deliveryCount: selectedDeliveryCount,
                  expectedCount: "",
                  shippedCount: "",
                  receivedCount: "",
                  expiryDate: null,
                  storageLocation: "",
                }
              }
              onChange={handleProductSetValueChange}
              onDelete={handleDeleteRow}
              isRegistration={false}
              isDisabled={isGeneralUser(loginUser?.permissionId)}
            />
          ))}

          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            gap={1}
            mb={2}
            px={2}
          >
            <Button
              variant="contained"
              sx={styles.orangeButton}
              onClick={() => {
                setSelectedJan(null);
                handleAddRow();
              }}
              disabled={isGeneralUser(loginUser?.permissionId)}
            >
              セットを追加する
            </Button>
          </Box>
          <Box>
            <NoteEditor
              userName={`${loginUser?.lastName ?? ""} ${
                loginUser?.firstName ?? ""
              }`}
              content={String(selectedMemo) ?? undefined}
              notes={notes}
              setNotes={setNotes}
              loginUser={loginUser}
              statusId={statusId}
            />
          </Box>
          {(isNotGeneralUser(loginUser?.permissionId) ||
            statusId === Status.PendingReceipt) && (
            <Button
              onClick={handleRegistItems}
              variant="contained"
              sx={styles.floatingRegisterButton}
              disabled={isUpdateLoading}
            >
              {statusId === Status.TrustBox
                ? isUpdateLoading
                  ? "復元中..."
                  : "復元"
                : isUpdateLoading
                ? "更新中..."
                : "更新"}
            </Button>
          )}
          {isGeneralUser(loginUser?.permissionId) && statusId === Status.NeedsReview && (
            <Button
              onClick={handleRegistItems}
              variant="contained"
              sx={styles.floatingRegisterButton}
              disabled={isUpdateLoading}
            >
              {isUpdateLoading ? "更新中..." : "倉庫ST更新"}
            </Button>
          )}
          <Snackbar
            open={toastOpen}
            // 同期結果の案内（FBA対象0件など）は読めるよう長めに表示する
            autoHideDuration={toastSeverity === "warning" ? 15000 : 1500}
            onClose={handleCloseToast}
            anchorOrigin={{ vertical: "top", horizontal: "center" }}
          >
            <MuiAlert
              onClose={handleCloseToast}
              severity={toastSeverity}
              sx={{ width: "100%" }}
              elevation={6}
              variant="filled"
            >
              <span dangerouslySetInnerHTML={{ __html: toastMessage }} />
            </MuiAlert>
          </Snackbar>
        </Dialog>
      </LocalizationProvider>
    </>
  );
};

export default ProductInfoDialog;
